import React, { useState, useRef, useEffect } from "react";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
function loadGoogleMaps() {
  if (!GMAPS_KEY) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (window.__gmapsPromise) return window.__gmapsPromise;
  window.__gmapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.__gmapsPromise;
}

const empty = {
  company_name: "", kvk_number: "", contact_person: "", email: "", phone: "",
  billing_address: "", delivery_address: "", postal_code: "", city: "", country: "Nederland",
  notes: "", accept_terms: false, accept_privacy: false,
};

export default function OrderForm({ products = [] }) {
  const [form, setForm] = useState(empty);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const billingRef = useRef(null);

  useEffect(() => {
    let ac;
    loadGoogleMaps().then((g) => {
      if (!g || !billingRef.current) return;
      ac = new g.maps.places.Autocomplete(billingRef.current, {
        fields: ["address_components", "formatted_address"],
        componentRestrictions: { country: ["nl"] },
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const comp = {};
        (place.address_components || []).forEach((c) => c.types.forEach((t) => (comp[t] = c.long_name)));
        setForm((f) => ({
          ...f,
          billing_address: place.formatted_address || f.billing_address,
          postal_code: comp.postal_code || f.postal_code,
          city: comp.locality || comp.postal_town || f.city,
          country: comp.country || f.country,
        }));
      });
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const totals = products.reduce((acc, p) => {
    const q = qty[p.id] || 0;
    const line = p.price_ex_vat * q;
    acc.sub += line;
    acc.vat += line * (p.vat_rate / 100);
    return acc;
  }, { sub: 0, vat: 0 });
  const total = totals.sub + totals.vat;

  const submit = async (e) => {
    e.preventDefault();
    const items = products.filter((p) => (qty[p.id] || 0) > 0).map((p) => ({ product_id: p.id, quantity: qty[p.id] }));
    if (items.length === 0) return toast.error("Selecteer minimaal één product.");
    if (!form.accept_terms || !form.accept_privacy) return toast.error("Akkoord met voorwaarden en privacy is verplicht.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, items });
      setResult(data);
      if (data.checkout_url) {
        toast.success("Bedrijf geverifieerd — u wordt doorgestuurd naar de betaling.");
        setTimeout(() => { window.location.href = data.checkout_url; }, 1400);
      } else {
        toast.info("Uw aanvraag wordt handmatig gecontroleerd.");
      }
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-white/10 bg-sfc-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none";

  if (result && !result.checkout_url) {
    return (
      <section id="bestellen" className="bg-[#120726] px-5 py-28 lg:px-10">
        <div className="mx-auto max-w-2xl rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <ShieldCheck className="mx-auto text-sfc-cyan" size={48} />
          <h2 className="mt-6 font-display text-3xl text-white">Aanvraag ontvangen</h2>
          <p className="mt-3 text-white/70">
            Ordernummer <span className="font-semibold text-sfc-orange">{result.order_number}</span>. Uw bedrijfsgegevens worden gecontroleerd.
            Zodra de verificatie is afgerond ontvangt u per e-mail en WhatsApp een veilige betaallink.
          </p>
          <button data-testid="order-reset" onClick={() => { setResult(null); setForm(empty); setQty({}); }} className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
            Nieuwe bestelling
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="bestellen" className="relative bg-[#120726] px-5 py-28 lg:px-10">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-sfc-yellow">Zakelijk bestellen</span>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">Plaats uw zakelijke bestelling</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-white/60">
              Uitsluitend voor zakelijke klanten. Na succesvolle bedrijfsverificatie ontvangt u een veilige iDEAL/kaart-betaallink.
            </p>
          </div>
        </Reveal>

        <form data-testid="order-form" onSubmit={submit} className="mt-12 grid gap-8 lg:grid-cols-5">
          <div className="grid gap-4 lg:col-span-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="order-company" required placeholder="Bedrijfsnaam" className={field} value={form.company_name} onChange={set("company_name")} />
              <input data-testid="order-kvk" required placeholder="KvK-nummer (8 cijfers)" className={field} value={form.kvk_number} onChange={set("kvk_number")} />
              <input data-testid="order-contact" required placeholder="Contactpersoon" className={field} value={form.contact_person} onChange={set("contact_person")} />
              <input data-testid="order-email" required type="email" placeholder="E-mailadres" className={field} value={form.email} onChange={set("email")} />
              <input data-testid="order-phone" required placeholder="Telefoonnummer" className={field} value={form.phone} onChange={set("phone")} />
              <input data-testid="order-country" placeholder="Land" className={field} value={form.country} onChange={set("country")} />
              <input data-testid="order-billing" ref={billingRef} required placeholder="Factuuradres (begin te typen voor suggesties)" autoComplete="off" className={`${field} sm:col-span-2`} value={form.billing_address} onChange={set("billing_address")} />
              <input data-testid="order-delivery" required placeholder="Afleveradres" className={`${field} sm:col-span-2`} value={form.delivery_address} onChange={set("delivery_address")} />
              <input data-testid="order-postal" required placeholder="Postcode" className={field} value={form.postal_code} onChange={set("postal_code")} />
              <input data-testid="order-city" required placeholder="Plaats" className={field} value={form.city} onChange={set("city")} />
            </div>
            <textarea data-testid="order-notes" rows={2} placeholder="Opmerkingen (optioneel)" className={field} value={form.notes} onChange={set("notes")} />
            <label className="flex items-start gap-2 text-sm text-white/70">
              <input data-testid="order-accept-terms" type="checkbox" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-0.5 h-4 w-4 accent-sfc-orange" />
              Ik ga akkoord met de <a href="/voorwaarden" target="_blank" className="text-sfc-orange hover:underline">algemene voorwaarden</a>.
            </label>
            <label className="flex items-start gap-2 text-sm text-white/70">
              <input data-testid="order-accept-privacy" type="checkbox" checked={form.accept_privacy} onChange={set("accept_privacy")} className="mt-0.5 h-4 w-4 accent-sfc-orange" />
              Ik ga akkoord met het <a href="/privacy" target="_blank" className="text-sfc-orange hover:underline">privacybeleid</a>.
            </label>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-28 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
              <h3 className="font-display text-lg text-white">Producten</h3>
              <div className="mt-4 space-y-4">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-white" style={{ color: p.accent_color }}>{p.name}</p>
                      <p className="text-xs text-white/50">€{p.price_ex_vat?.toFixed(2)} · min. {p.minimum_quantity}</p>
                    </div>
                    <input
                      data-testid={`order-qty-${p.slug}`}
                      type="number" min={0} value={qty[p.id] || 0}
                      onChange={(e) => setQty({ ...qty, [p.id]: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                      className="w-20 rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-center text-sm text-white focus:border-sfc-orange focus:outline-none"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-1 border-t border-white/10 pt-4 text-sm text-white/70">
                <div className="flex justify-between"><span>Subtotaal excl. btw</span><span data-testid="order-subtotal">€{totals.sub.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Btw</span><span>€{totals.vat.toFixed(2)}</span></div>
                <div className="flex justify-between pt-1 font-display text-lg text-white"><span>Totaal</span><span data-testid="order-total">€{total.toFixed(2)}</span></div>
              </div>
              <button data-testid="order-submit" disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <>Verstuur aanvraag <ArrowRight size={16} /></>}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/40"><ShieldCheck size={13} /> Veilige verwerking · server-side verificatie</p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
